import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  CalendarDays,
  Clock3,
  CreditCard,
  HeartPulse,
  RefreshCw,
  Search,
  Stethoscope,
  UserPlus,
  UsersRound,
} from "lucide-react";
import SidebarLayout from "../../components/SidebarLayout";
import { BACKEND_URL } from "../../config";
import { useAuth } from "../../modules/auth/authContext";
import { formatPatientName } from "../../utils/patientName";
import { formatVisitLabel } from "../../utils/visitNumber";

interface Visit {
  id: number;
  visit_number?: string;
  patient_id: number;
  status: string;
  priority?: string;
  visit_date?: string;
  createdAt?: string;
  assigned_doctor_id?: number;
  patient?: {
    id: number;
    patient_code?: string;
    Contact?: { salutation?: string; name?: string; phone?: string };
  };
  assigned_doctor?: { name?: string };
  vitals?: any[];
}

const statusProgress: Record<string, number> = {
  CHECKED_IN: 1,
  VITALS_PENDING: 1,
  VITALS_DONE: 2,
  DOCTOR_ASSIGNED: 3,
  WAITING_FOR_DOCTOR: 3,
  IN_CONSULTATION: 3,
  CONSULTATION_DONE: 4,
  LAB_REQUESTED: 4,
  BILLING_PENDING: 4,
  BILLING_IN_PROGRESS: 4,
  PAYMENT_PENDING: 4,
  PAID: 5,
  COMPLETED: 5,
};

const labelForStatus = (status: string) =>
  status
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const minutesSince = (value?: string) => {
  if (!value) return "0m";
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const todayString = () => {
  const now = new Date();
  const offsetDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 10);
};

const withinDateRange = (value: string | undefined, dateFrom: string, dateTo: string) => {
  if (!dateFrom || !dateTo) return true;
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return false;
  const start = new Date(`${dateFrom}T00:00:00`).getTime();
  const end = new Date(`${dateTo}T23:59:59`).getTime();
  return timestamp >= start && timestamp <= end;
};

const Dashboard: React.FC = () => {
  const { user, hasRole } = useAuth();
  const defaultDate = todayString();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [attendance, setAttendance] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultDate);
  const [dateTo, setDateTo] = useState(defaultDate);
  const [loading, setLoading] = useState(false);

  const isAdmin = hasRole("admin");
  const isDoctor = hasRole("doctor");
  const isFrontDesk = hasRole(["front_desk", "nurse", "staff"]);
  const isBilling = hasRole("billing");

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const visitParams = new URLSearchParams({
        date_from: dateFrom,
        date_to: dateTo,
      });
      if (isDoctor && user?.employee_id) visitParams.set("doctor_id", String(user.employee_id));

      const attendanceParams = new URLSearchParams({
        date_from: dateFrom,
        date_to: dateTo,
      });

      const [visitRes, billRes, attendanceRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/visits?${visitParams.toString()}`, { credentials: "include" }),
        isAdmin || isBilling ? fetch(`${BACKEND_URL}/api/billing`, { credentials: "include" }) : Promise.resolve(null),
        isAdmin || isFrontDesk ? fetch(`${BACKEND_URL}/api/attendance?${attendanceParams.toString()}`, { credentials: "include" }) : Promise.resolve(null),
      ]);

      if (visitRes.ok) setVisits(await visitRes.json());
      if (billRes && billRes.ok) setBills(await billRes.json());
      if (attendanceRes && attendanceRes.ok) setAttendance(await attendanceRes.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [user?.employee_id, user?.role, dateFrom, dateTo]);

  const resetToToday = () => {
    const today = todayString();
    setDateFrom(today);
    setDateTo(today);
  };

  const visibleVisits = useMemo(() => {
    let next = visits;
    if (isFrontDesk && !isAdmin) {
      next = visits.filter((visit) => ["CHECKED_IN", "VITALS_PENDING", "VITALS_DONE", "WAITING_FOR_DOCTOR"].includes(visit.status));
    }
    if (isBilling && !isAdmin) {
      next = visits.filter((visit) => ["BILLING_PENDING", "BILLING_IN_PROGRESS", "PAYMENT_PENDING", "PAID"].includes(visit.status));
    }

    if (!search.trim()) return next;
    const query = search.toLowerCase();
    return next.filter((visit) => {
      const name = formatPatientName(visit.patient?.Contact, "");
      const code = visit.patient?.patient_code || String(visit.patient_id);
      return `${name} ${code} ${visit.status}`.toLowerCase().includes(query);
    });
  }, [visits, search, isAdmin, isBilling, isFrontDesk]);

  const metrics = [
    { label: "Total Visits", value: visits.length, icon: UsersRound },
    { label: "Waiting", value: visits.filter((v) => ["CHECKED_IN", "VITALS_PENDING", "WAITING_FOR_DOCTOR"].includes(v.status)).length, icon: Clock3 },
    { label: "In Consultation", value: visits.filter((v) => v.status === "IN_CONSULTATION").length, icon: Stethoscope },
    { label: "Billing", value: visits.filter((v) => ["BILLING_PENDING", "BILLING_IN_PROGRESS", "PAYMENT_PENDING"].includes(v.status)).length, icon: CreditCard },
    { label: "Completed", value: visits.filter((v) => ["PAID", "COMPLETED"].includes(v.status)).length, icon: CheckCircle2 },
  ];

  const visibleBills = useMemo(
    () => bills.filter((bill) => withinDateRange(bill.date, dateFrom, dateTo)),
    [bills, dateFrom, dateTo]
  );

  const rangeLabel = dateFrom === dateTo ? dateFrom : `${dateFrom} to ${dateTo}`;

  return (
    <SidebarLayout title="Dashboard" subtitle="Live clinic workflow based on your access.">
      <div className="flex w-full flex-col gap-6">
        <section className="grid grid-cols-1 gap-5 rounded border border-border-light bg-surface-light p-5 xl:grid-cols-[minmax(280px,1fr)_minmax(0,980px)] xl:items-end">
          <div className="min-w-0">
            <div className="mb-2 text-sm font-semibold text-primary">{user?.role || "clinic"} view</div>
            <h2 className="text-2xl font-black text-text-light">Patient Flow</h2>
            <p className="mt-1 text-sm text-subtext-light">Statuses update from check-in through payment collection.</p>
          </div>
          <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-2 xl:grid-cols-[145px_145px_110px_minmax(230px,1fr)_112px_auto]">
            <label className="block min-w-0">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-subtext-light">From</span>
              <input
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(event) => setDateFrom(event.target.value)}
                className="h-11 w-full rounded border border-border-light bg-background-light px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
            <label className="block min-w-0">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-subtext-light">To</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(event) => setDateTo(event.target.value)}
                className="h-11 w-full rounded border border-border-light bg-background-light px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
            <button
              type="button"
              onClick={resetToToday}
              className="inline-flex h-11 items-center justify-center gap-2 rounded border border-border-light px-4 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary"
            >
              <CalendarDays size={18} />
              Today
            </button>
            <label className="block min-w-0">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-subtext-light">Search</span>
              <span className="relative block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-subtext-light" size={18} />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="h-11 w-full rounded border border-border-light bg-background-light pl-10 pr-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  placeholder="Search patients or status"
                />
              </span>
            </label>
            <button onClick={loadDashboard} className="inline-flex h-11 items-center justify-center gap-2 rounded border border-border-light px-4 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
              <RefreshCw size={18} />
              Refresh
            </button>
            {isFrontDesk && (
              <a href="/patients/intake" className="inline-flex h-11 items-center justify-center gap-2 rounded bg-primary px-4 font-semibold text-white hover:bg-primary/90">
                <UserPlus size={18} />
                Check In
              </a>
            )}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <article key={metric.label} className="rounded border border-border-light border-l-4 border-l-primary bg-surface-light p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-subtext-light">{metric.label}</p>
                  <Icon className="text-subtle-light" size={20} />
                </div>
                <h3 className="text-3xl font-black text-text-light">{metric.value}</h3>
              </article>
            );
          })}
        </section>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <section className="rounded border border-border-light bg-surface-light p-5">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-xl font-black">Queue</h3>
                <p className="text-sm text-subtext-light">{loading ? "Loading..." : `${visibleVisits.length} visible visits for ${rangeLabel}`}</p>
              </div>
            </div>
            <div className="overflow-x-auto rounded bg-background-light p-2">
              <table className="w-full min-w-[760px] border-separate border-spacing-y-3 text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-subtext-light">
                    <th className="px-4 py-2">Patient</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Wait</th>
                    <th className="px-4 py-2">Assigned</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleVisits.map((visit) => {
                    const name = formatPatientName(visit.patient?.Contact, `Patient ${visit.patient_id}`);
                    const initials = name.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
                    const progress = statusProgress[visit.status] || 1;
                    return (
                      <tr key={visit.id} className="bg-surface-light">
                        <td className="rounded-l px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`flex h-10 w-10 items-center justify-center rounded-full font-bold ${visit.priority === "urgent" ? "bg-alert-red/10 text-alert-red" : "bg-primary/10 text-primary"}`}>
                              {initials || "PT"}
                            </div>
                            <div>
                              <div className="font-bold">{name}</div>
                              <div className="text-xs text-subtext-light">{formatVisitLabel(visit)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-4">
                            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
                              {labelForStatus(visit.status)}
                            </span>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((step) => (
                                <div key={step} className={`h-1 w-8 rounded-full ${step <= progress ? "bg-primary" : "bg-border-light"}`} />
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="inline-flex items-center gap-2 text-sm font-bold text-text-light">
                            <Clock3 size={16} />
                            {minutesSince(visit.createdAt || visit.visit_date)}
                          </span>
                        </td>
                        <td className="rounded-r px-4 py-4 text-sm font-medium text-text-light">{visit.assigned_doctor?.name || "Unassigned"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <aside className="flex flex-col gap-6">
            {(isAdmin || isFrontDesk) && (
              <section className="rounded border border-border-light bg-surface-light p-5">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
                  <HeartPulse className="text-primary" size={20} />
                  Staff Present
                </h3>
                <div className="text-3xl font-black">{attendance.filter((item) => item.status === "present").length}</div>
                <p className="mt-1 text-sm text-subtext-light">{attendance.length} attendance records for {rangeLabel}</p>
              </section>
            )}

            {(isAdmin || isBilling) && (
              <section className="rounded border border-border-light bg-surface-light p-5">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
                  <CreditCard className="text-primary" size={20} />
                  Billing
                </h3>
                <div className="text-3xl font-black">{visibleBills.length}</div>
                <p className="mt-1 text-sm text-subtext-light">Bills for {rangeLabel}</p>
              </section>
            )}
          </aside>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default Dashboard;
